import React, { useEffect, useMemo, useState } from "react";
import { CaseLink } from "@/components/CaseLink";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { StageChip } from "@/components/StageChip";
import { loadMeta } from "@/lib/meta";
import { FIELDS } from "@/lib/fields";
import { formatINR, banksLabel, fmtDate, fmtLastConv, isOverdue, daysSince } from "@/lib/format";
import { toast } from "sonner";
import { Search, RefreshCw, Loader2, AlertCircle, Users, Inbox, SlidersHorizontal } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { NewLeadButton } from "@/components/NewLead";

const TABS = [
  { key: "active", label: "Active" },
  { key: "disbursed", label: "Disbursed" },
  { key: "lost", label: "Lost" },
];

const TRUVA_OPTS = [{ v: "Yes", l: "Truva" }, { v: "No", l: "Out of Truva" }];

function buildQuarters() {
  const qs = ["JFM", "AMJ", "JAS", "OND"];
  const now = new Date();
  const endYear = now.getFullYear() + 1;
  const out = [];
  for (let y = 2025; y <= endYear; y++) {
    const yy = String(y).slice(-2);
    qs.forEach((q) => out.push(`${q}-${yy}`));
  }
  return out;
}
const QUARTER_OPTS = buildQuarters();

export default function MyCases() {
  const { user, activeRole } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("active");
  const [q, setQ] = useState("");
  const [meta, setMeta] = useState({});
  const [filters, setFilters] = useState({ stages: [], rm: "", bank: "", truva: "", partner: "", overdue: false, quarter: "", property: "" });
  const [consulted, setConsulted] = useState(() => new URLSearchParams(window.location.search).get("view") === "consulted"); // saved view ?view=consulted (F-C1)
  useEffect(() => {
    const v = new URLSearchParams(loc.search).get("view");
    setConsulted(v === "consulted");
    if (v === "stuck") setFilters((f) => ({ ...f, overdue: true }));
  }, [loc.search]);
  useEffect(() => { loadMeta().then((m) => setMeta(m.selects || {})); }, []);
  const activeFilterCount = filters.stages.length + ["rm", "bank", "truva", "partner", "quarter", "property"].filter((k) => filters[k]).length + (filters.overdue ? 1 : 0);
  const resetFilters = () => setFilters({ stages: [], rm: "", bank: "", truva: "", partner: "", overdue: false, quarter: "", property: "" });
  const [updates, setUpdates] = useState([]);
  const [ack, setAck] = useState(null);
  const [shown, setShown] = useState(50);
  const [logFor, setLogFor] = useState(null);
  useEffect(() => { setShown(50); }, [q, filters, tab, consulted]);

  const loadUpdates = async () => {
    try { setUpdates((await api.get("/partner-updates", { params: { status: "unacknowledged" } })).data.updates); }
    catch { /* non-blocking */ }
  };
  useEffect(() => { loadUpdates(); }, []);

  const isManagerView = activeRole === "Manager" || activeRole === "Admin";
  const scope = isManagerView ? "all" : "mine";

  const load = async (refresh = false) => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get("/cases", { params: { scope: consulted ? "consulted_newlead" : scope, refresh } });
      setCases(data.cases);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load cases. Is AIRTABLE_PAT configured?");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, consulted]);

  const baseList = useMemo(() => {
    let list = q.trim()
      ? cases.filter((c) => {
          const s = q.trim().toLowerCase();
          return [c.name, c.borrower, c.case_id, c.phone].some((v) => (v || "").toLowerCase().includes(s));
        })
      : cases;
    const F = filters;
    if (F.stages.length) list = list.filter((c) => F.stages.includes(c.stage));
    if (F.rm) list = list.filter((c) => (Array.isArray(c.hl_rm) ? c.hl_rm.includes(F.rm) : c.hl_rm === F.rm));
    if (F.bank) list = list.filter((c) => (Array.isArray(c.banks) ? c.banks.includes(F.bank) : c.banks === F.bank));
    if (F.truva) list = list.filter((c) => c.truva_non_truva === F.truva);
    if (F.property) list = list.filter((c) => (c.property_bucket || "Unknown") === F.property);
    if (F.partner) list = list.filter((c) => c.partner_company === F.partner);
    if (F.quarter) list = list.filter((c) => c.quarter === F.quarter);
    if (F.overdue) list = list.filter((c) => c.stage_group === "active" && isOverdue(c.follow_up_date));
    return list;
  }, [cases, q, filters]);

  const filtered = useMemo(() => {
    const list = (q.trim() || consulted) ? baseList : baseList.filter((c) => c.stage_group === tab);
    return [...list].sort((a, b) => {
      const aa = a.stage_group === "active", ba = b.stage_group === "active";
      const ao = aa && isOverdue(a.follow_up_date) ? 0 : 1;
      const bo = ba && isOverdue(b.follow_up_date) ? 0 : 1;
      if (ao !== bo) return ao - bo;
      const af = a.follow_up_date ? new Date(a.follow_up_date).getTime() : Infinity;
      const bf = b.follow_up_date ? new Date(b.follow_up_date).getTime() : Infinity;
      if (af !== bf) return af - bf;
      return (b.modified_time || "").localeCompare(a.modified_time || "");
    });
  }, [baseList, tab, q, consulted]);

  const rmOptions = useMemo(() => {
    const set = new Set();
    cases.forEach((c) => { (Array.isArray(c.hl_rm) ? c.hl_rm : [c.hl_rm]).forEach((r) => r && set.add(r)); });
    return [...set].sort();
  }, [cases]);

  const counts = useMemo(() => {
    const c = { active: 0, disbursed: 0, lost: 0 };
    baseList.forEach((x) => { c[x.stage_group] = (c[x.stage_group] || 0) + 1; });
    return c;
  }, [baseList]);

  const summary = useMemo(() => {
    if (!isManagerView) return null;
    const rows = {};
    baseList.filter((c) => !c.is_test).forEach((c) => {
      const rm = (consulted ? c.consultation_taken_by : (Array.isArray(c.hl_rm) ? c.hl_rm[0] : c.hl_rm)) || "Unassigned";
      rows[rm] = rows[rm] || { active: 0, disbursed: 0, lost: 0, total: 0 };
      rows[rm][c.stage_group] = (rows[rm][c.stage_group] || 0) + 1;
      rows[rm].total += 1;
    });
    return Object.entries(rows).sort((a, b) => b[1].total - a[1].total);
  }, [baseList, isManagerView, consulted]);

  return (
    <Layout>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[#333333]">
            {isManagerView ? "All Cases" : "My Cases"}
          </h1>
          <p className="text-sm text-[#6B7280] mt-0.5 flex items-center gap-1.5">
            {isManagerView && <Users className="w-4 h-4 text-[#FF4802]" />}
            {isManagerView
              ? `${cases.length} cases across all RMs`
              : `${cases.length} cases assigned to ${user?.hl_rm_value || user?.name}`}
          </p>
        </div>
        <NewLeadButton />
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-[#C5C4C4] absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, phone, case ID…"
              className="pl-8 h-9 w-72 text-sm bg-white border-[#E2E2E2] rounded-lg"
              data-testid="case-search-input"
            />
          </div>
          <button
            onClick={() => load(true)}
            data-testid="refresh-button"
            className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#E2E2E2] bg-white text-sm text-[#333333] hover:bg-[#F7F7F7] transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {updates.length > 0 && (
        <div className="rounded-2xl border border-[#F5B71D]/40 bg-[#F5B71D]/10 p-4 mb-4" data-testid="updates-inbox">
          <div className="flex items-center gap-2 text-sm font-medium text-[#92600a] mb-2"><Inbox className="w-4 h-4" /> Partner updates ({updates.length})</div>
          <div className="space-y-2">
            {updates.map((u) => {
              const old = (Date.now() - new Date(u.created_at).getTime()) > 86400000;
              return (
                <div key={u.id} className={`flex items-center justify-between bg-white border rounded-lg px-3 py-2 text-xs ${old ? "border-[#CA1212]/40" : "border-[#E2E2E2]"}`} data-testid={`update-${u.case_id}`}>
                  <div>
                    <CaseLink recordId={u.case_record_id} className="font-mono font-semibold text-[#E2521B] hover:underline">{u.case_id}</CaseLink>
                    <span className="text-[#6B7280] ml-2">{u.partner_company} ({u.spoc_name}): {u.text}</span>
                    {old && <span className="ml-2 text-[#CA1212] uppercase text-[10px]">&gt;24h</span>}
                  </div>
                  <button onClick={() => setAck(u)} data-testid={`ack-${u.case_id}`} className="text-xs bg-[#FF4802] text-white px-3 py-1.5 rounded-lg hover:bg-[#E2521B]">Acknowledge</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {summary && (
        <div className="rounded-2xl border border-[#E2E2E2] bg-white p-4 mb-4 shadow-sm overflow-x-auto" data-testid="manager-summary">
          <h3 className="text-xs font-medium text-[#6B7280] mb-3 uppercase" style={{ letterSpacing: "0.06em" }}>{consulted ? "Consulted leads per consultant" : "Cases per RM × stage"}</h3>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[#6B7280] border-b border-[#E2E2E2]">{[consulted ? "Consulted by" : "RM", "Active", "Disbursed", "Lost", "Total"].map((h) => <th key={h} className="py-1.5 pr-4 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {summary.map(([rm, s]) => (
                <tr key={rm} className="border-b border-[#F7F7F7]" data-testid={`summary-row-${rm}`}>
                  <td className="py-1.5 pr-4 font-medium text-[#333333]">{rm}</td>
                  <td className="py-1.5 pr-4 text-[#6B7280]">{s.active}</td>
                  <td className="py-1.5 pr-4 text-[#159A50]">{s.disbursed}</td>
                  <td className="py-1.5 pr-4 text-[#CA1212]">{s.lost}</td>
                  <td className="py-1.5 pr-4 font-semibold text-[#333333]">{s.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-2xl border border-[#E2E2E2] bg-white p-3 mb-4 shadow-sm flex flex-wrap items-center gap-2" data-testid="filter-bar">
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="filters-button" className={`flex items-center gap-1.5 text-xs rounded-lg px-3 py-1.5 border font-medium ${activeFilterCount ? "bg-[#FF4802] text-white border-[#FF4802]" : "bg-white text-[#333333] border-[#E2E2E2] hover:border-[#FF6F38]"}`}>
              <SlidersHorizontal className="w-3.5 h-3.5" /> Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[420px] bg-white rounded-2xl border-[#E2E2E2] truva-popup-shadow p-4 space-y-3" data-testid="filters-popover">
            <div className="grid grid-cols-2 gap-2">
              <FilterSelect testid="filter-rm" placeholder="All RMs" value={filters.rm} onChange={(v) => setFilters({ ...filters, rm: v })} options={rmOptions} />
              <FilterSelect testid="filter-bank" placeholder="All banks" value={filters.bank} onChange={(v) => setFilters({ ...filters, bank: v })} options={meta[FIELDS.BANK_NAME] || []} />
              <FilterSelect testid="filter-partner" placeholder="All partners" value={filters.partner} onChange={(v) => setFilters({ ...filters, partner: v })} options={meta[FIELDS.PARTNER_COMPANY] || []} />
              <FilterSelect testid="filter-quarter" placeholder="All quarters" value={filters.quarter} onChange={(v) => setFilters({ ...filters, quarter: v })} options={QUARTER_OPTS} />
              <FilterSelect testid="filter-property" placeholder="Property: any" value={filters.property} onChange={(v) => setFilters({ ...filters, property: v })} options={["Finalised", "Not finalised", "Unknown"]} />
              <Select value={filters.truva || "all"} onValueChange={(v) => setFilters({ ...filters, truva: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="filter-truva" className="h-8 w-full text-xs border-[#E2E2E2] rounded-lg bg-white"><SelectValue placeholder="Truva / Out" /></SelectTrigger>
                <SelectContent className="bg-white rounded-xl border-[#E2E2E2]">
                  <SelectItem value="all">Truva & Out</SelectItem>
                  {TRUVA_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                </SelectContent>
              </Select>
              <label className="flex items-center gap-1.5 text-xs text-[#333333] cursor-pointer px-2" data-testid="filter-overdue">
                <input type="checkbox" className="accent-[#FF4802] w-4 h-4" checked={filters.overdue} onChange={(e) => setFilters({ ...filters, overdue: e.target.checked })} />
                Overdue follow-up
              </label>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="filter-stages">
              {(meta[FIELDS.LEAD_STAGE] || []).map((st) => {
                const on = filters.stages.includes(st);
                return (
                  <button key={st} onClick={() => setFilters({ ...filters, stages: on ? filters.stages.filter((x) => x !== st) : [...filters.stages, st] })}
                    className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${on ? "bg-[#FF4802] text-white border-[#FF4802]" : "bg-white text-[#6B7280] border-[#E2E2E2] hover:border-[#FF6F38]"}`}>{st}</button>
                );
              })}
            </div>
            {activeFilterCount > 0 && <button onClick={resetFilters} data-testid="filter-reset" className="text-xs text-[#FF4802] hover:underline">Clear all filters</button>}
          </PopoverContent>
        </Popover>
        <button onClick={() => setConsulted((v) => !v)} data-testid="filter-consulted-newlead" title="Consultation Taken By = me · stage New Lead · Consultation Completed"
          className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${consulted ? "bg-[#FF4802] text-white border-[#FF4802]" : "bg-white text-[#6B7280] border-[#E2E2E2] hover:border-[#FF6F38]"}`}>
          {isManagerView ? "Consulted · still New Lead" : "My consulted leads"}{consulted ? ` (${filtered.length})` : ""}
        </button>
        {/* only ACTIVE filters show as chips outside the popover */}
        {[["rm", filters.rm], ["bank", filters.bank], ["partner", filters.partner], ["quarter", filters.quarter], ["property", filters.property ? `Property ${filters.property.toLowerCase()}` : ""], ["truva", filters.truva ? TRUVA_OPTS.find((o) => o.v === filters.truva)?.l : ""], ["overdue", filters.overdue ? "Overdue" : ""]]
          .filter(([, v]) => v).map(([k, v]) => (
            <button key={k} onClick={() => setFilters({ ...filters, [k]: k === "overdue" ? false : "" })} data-testid={`chip-${k}`} className="text-[11px] px-2.5 py-1 rounded-full bg-[#FF4802]/10 text-[#E2521B] border border-[#FF4802]/30">{v} ✕</button>
          ))}
        {filters.stages.map((st) => <button key={st} onClick={() => setFilters({ ...filters, stages: filters.stages.filter((x) => x !== st) })} data-testid={`chip-stage-${st}`} className="text-[11px] px-2.5 py-1 rounded-full bg-[#FF4802]/10 text-[#E2521B] border border-[#FF4802]/30">{st} ✕</button>)}
      </div>

      {!q && !consulted && (
        <div className="flex space-x-1 bg-[#E2E2E2]/50 p-1 rounded-xl w-fit mb-4">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`text-xs px-3.5 py-1.5 rounded-lg transition-all font-medium ${
                tab === t.key ? "bg-white text-[#333333] shadow-sm" : "text-[#6B7280] hover:text-[#333333]"
              }`}
            >
              {t.label} <span className="text-[#C5C4C4] ml-1">{counts[t.key] || 0}</span>
            </button>
          ))}
        </div>
      )}

      {err ? (
        <div className="flex items-center gap-3 rounded-2xl border border-[#F5B71D]/40 bg-[#F5B71D]/10 text-[#92600a] px-4 py-3 text-sm" data-testid="cases-error">
          <AlertCircle className="w-5 h-5" /> {err}
        </div>
      ) : loading ? (
        <div className="flex items-center gap-2 text-[#6B7280] text-sm py-12 justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#FF4802]" /> Loading cases…
        </div>
      ) : consulted ? (
        <div className="w-full overflow-hidden rounded-2xl border border-[#E2E2E2] bg-white shadow-sm" data-testid="consulted-table">
          <table className="w-full text-xs">
            <thead><tr>{["Case", "Name", "Consulted by", "Purchase status", "Last conversation", "Follow-up", "Next action"].map((h) => (
              <th key={h} className="bg-[#F7F7F7] text-[#6B7280] font-medium text-xs py-3 px-3.5 text-left border-b border-[#E2E2E2] uppercase tracking-wider">{h}</th>))}</tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={7} className="text-center text-sm text-[#6B7280] py-10">No consulted leads waiting.</td></tr>}
              {[...filtered].sort((a, b) => (a.follow_up_date || "9999").localeCompare(b.follow_up_date || "9999")).map((c) => {
                const leak = !c.follow_up_date || daysSince(c.follow_up_date) > 2;
                return (
                  <tr key={c.record_id} onClick={() => navigate(`/cases/${c.record_id}`)} data-testid={`consulted-row-${c.case_id}`}
                    className={`border-b border-[#F7F7F7] hover:bg-[#F6F5EF]/70 cursor-pointer ${leak ? "bg-[#CA1212]/5" : ""}`}>
                    <td className="py-2.5 px-3.5 whitespace-nowrap"><CaseLink recordId={c.record_id} className="font-mono font-semibold text-[#E2521B] hover:underline">{c.case_id}</CaseLink></td>
                    <td className="py-2.5 px-3.5 font-medium text-[#333333]">{c.borrower || c.name || "—"}</td>
                    <td className="py-2.5 px-3.5 text-[#6B7280]">{c.consultation_taken_by || "—"}</td>
                    <td className="py-2.5 px-3.5 text-[#6B7280]">{c.purchase_status || "—"}</td>
                    <td className="py-2.5 px-3.5 text-[#6B7280] whitespace-nowrap">{fmtLastConv(c.last_conversation)}</td>
                    <td className={`py-2.5 px-3.5 whitespace-nowrap ${leak ? "text-[#CA1212] font-medium" : "text-[#333333]"}`}>{c.follow_up_date ? fmtDate(c.follow_up_date) : "No follow-up"}{leak && c.follow_up_date ? " · overdue" : ""}</td>
                    <td className="py-2.5 px-3.5 text-[#6B7280] max-w-[220px] truncate">{c.next_action || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="w-full overflow-hidden rounded-2xl border border-[#E2E2E2] bg-white shadow-sm">
          <table className="w-full">
            <thead>
              <tr>
                {["Case", "Name", "Stage", "Loan", "Bank(s)", (tab === "lost" && !q) ? "Lost reason / date" : "Follow-up", "Last conversation", isManagerView ? "RM" : ""].filter(Boolean).map((h) => (
                  <th key={h} className="bg-[#F7F7F7] text-[#6B7280] font-medium text-xs py-3 px-3.5 text-left border-b border-[#E2E2E2] uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center text-sm text-[#6B7280] py-10" data-testid="cases-empty">{q ? `No cases match “${q}”. Try the case ID, phone or a part of the name.` : activeFilterCount ? "No cases match these filters — clear one to widen the list." : "No cases in this view yet. Add one with “+ New lead”."}</td></tr>
              )}
              {filtered.slice(0, shown).map((c) => {
                const isActive = c.stage_group === "active";
                const overdue = isActive && isOverdue(c.follow_up_date);
                return (
                  <tr
                    key={c.record_id}
                    onClick={() => navigate(`/cases/${c.record_id}`)}
                    data-testid={`case-row-${c.case_id}`}
                    className={`border-b border-[#F7F7F7] hover:bg-[#F6F5EF]/70 transition-colors cursor-pointer text-xs ${overdue ? "bg-[#CA1212]/5" : ""}`}
                  >
                    <td className="py-2.5 px-3.5 whitespace-nowrap">
                      <CaseLink recordId={c.record_id} className="font-mono text-xs font-semibold text-[#E2521B] bg-[#FF4802]/5 px-2 py-0.5 rounded border border-[#FF4802]/20 hover:underline">{c.case_id}</CaseLink>
                    </td>
                    <td className="py-2.5 px-3.5 font-medium text-[#333333]">{c.borrower || c.name || "—"}</td>
                    <td className="py-2.5 px-3.5"><StageChip stage={c.stage} /></td>
                    <td className="py-2.5 px-3.5 font-mono font-semibold text-[#333333] whitespace-nowrap">{formatINR(c.loan_amount)}</td>
                    <td className="py-2.5 px-3.5 text-[#6B7280]">{banksLabel(c.banks)}</td>
                    {c.stage_group === "lost" ? (
                      <td className="py-2.5 px-3.5 text-[#6B7280]">
                        <span className="text-[#CA1212]">{c.lost_reason || "—"}</span>
                        {c.lost_date && <span className="text-[#C5C4C4] ml-1">· {fmtDate(c.lost_date)}</span>}
                      </td>
                    ) : c.stage_group === "disbursed" ? (
                      <td className="py-2.5 px-3.5 text-[#C5C4C4]">—</td>
                    ) : (
                      <td className={`py-2.5 px-3.5 ${overdue ? "text-[#CA1212] font-semibold" : "text-[#6B7280]"}`}>
                        {fmtDate(c.follow_up_date)}{overdue && <span className="ml-1 text-[10px] uppercase">overdue</span>}
                      </td>
                    )}
                    <td className="py-2.5 px-3.5 text-[#6B7280] whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <span data-testid={`lastconv-${c.case_id}`}>{fmtLastConv(c.last_conversation)}</span>
                      {isActive && <button onClick={() => setLogFor(c)} data-testid={`log-conv-${c.case_id}`} className="ml-2 text-[11px] text-[#FF4802] hover:underline">Log</button>}
                    </td>
                    {isManagerView && <td className="py-2.5 px-3.5 text-[#6B7280]">{Array.isArray(c.hl_rm) ? c.hl_rm.join(", ") : c.hl_rm}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length > shown && (
            <div className="px-4 py-3 border-t border-[#F7F7F7] flex items-center justify-between text-xs text-[#6B7280]">
              <span>Showing {shown} of {filtered.length}</span>
              <button onClick={() => setShown((s) => s + 50)} data-testid="cases-show-more" className="text-[#FF4802] hover:underline font-medium">Show 50 more</button>
            </div>
          )}
        </div>
      )}
      <AckDialog ack={ack} onClose={() => setAck(null)} onDone={() => { setAck(null); loadUpdates(); load(true); }} />
      <LogConvDialog c={logFor} onClose={() => setLogFor(null)} onDone={() => { setLogFor(null); load(true); }} />
    </Layout>
  );
}

function LogConvDialog({ c, onClose, onDone }) {
  const [type, setType] = useState("Call");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (c) { setType("Call"); setText(""); } }, [c]);
  if (!c) return null;
  const submit = async () => {
    if (!text.trim()) { toast.error("Add a line about the conversation"); return; }
    setSaving(true);
    try { await api.post(`/cases/${c.record_id}/note`, { type, text: text.trim(), follow_up_date: null, happened_on: null }); toast.success("Logged — Last conversation updated"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white rounded-2xl truva-popup-shadow" aria-describedby={undefined} data-testid="log-conv-dialog">
        <DialogHeader><DialogTitle className="text-[#333333] font-medium">Log a conversation · {c.case_id}</DialogTitle></DialogHeader>
        <div className="flex gap-1.5">{["Call", "WhatsApp", "Meeting"].map((t) => <button key={t} onClick={() => setType(t)} data-testid={`log-conv-type-${t}`} className={`text-xs px-3 py-1 rounded-full border ${type === t ? "bg-[#FF4802] text-white border-[#FF4802]" : "border-[#E2E2E2] text-[#6B7280]"}`}>{t}</button>)}</div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="log-conv-text" rows={3} placeholder="What was discussed?" className="w-full text-sm border border-[#E2E2E2] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF4802]/40" />
        <DialogFooter className="gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-[#E2E2E2] text-sm">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="log-conv-save" className="px-4 py-2 rounded-lg bg-[#FF4802] text-white text-sm font-medium disabled:opacity-50">{saving ? "Saving…" : "Log"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FilterSelect({ testid, placeholder, value, onChange, options }) {
  return (
    <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="h-8 w-40 text-xs border-[#E2E2E2] rounded-lg bg-white"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="bg-white rounded-xl border-[#E2E2E2] max-h-60">
        <SelectItem value="all">{placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function AckDialog({ ack, onClose, onDone }) {
  const navigate = useNavigate();
  const [stage, setStage] = useState("");
  const [stages, setStages] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (ack) { setStage(""); loadMeta().then((m) => setStages(m.selects?.[FIELDS.LEAD_STAGE] || [])); }
  }, [ack]);
  if (!ack) return null;
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/partner-updates/${ack.id}/acknowledge`, { stage: stage || null });
      toast.success("Acknowledged — appended to Case History");
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.open_stage_dialog) { toast.error(`${d.detail} — opening the Stage dialog`); onClose(); navigate(`/cases/${ack.case_record_id}?stage=${encodeURIComponent(d.stage)}`); return; }
      toast.error((typeof d === "string" && d) || "Failed");
    }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!ack} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white rounded-2xl truva-popup-shadow" aria-describedby={undefined} data-testid="ack-dialog">
        <DialogHeader><DialogTitle className="text-[#333333] font-medium">Acknowledge update · {ack.case_id}</DialogTitle></DialogHeader>
        <p className="text-sm text-[#6B7280]">{ack.partner_company} ({ack.spoc_name}): {ack.text}</p>
        <div>
          <div className="text-[11px] uppercase text-[#6B7280] mb-1" style={{ letterSpacing: "0.06em" }}>Optionally change stage</div>
          <Select value={stage} onValueChange={setStage}>
            <SelectTrigger data-testid="ack-stage-select" className="border-[#E2E2E2] rounded-lg"><SelectValue placeholder="Keep current stage" /></SelectTrigger>
            <SelectContent className="bg-white rounded-xl">{stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <DialogFooter className="gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-[#E2E2E2] text-sm text-[#333333] hover:bg-[#F7F7F7]">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="ack-confirm" className="px-4 py-2 rounded-lg bg-[#FF4802] text-white text-sm hover:bg-[#E2521B] disabled:opacity-50 font-medium">{saving ? "Saving…" : "Acknowledge"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
